// Copyright (c) Ping. All rights reserved.
//
// Pure markdown text-transform helpers for the Notes editor's formatting
// toolbar. The note `body` is treated as MARKDOWN source-of-truth — each
// toolbar action rewrites the body string and returns the next caret /
// selection range so the `TextInput` can re-place the cursor.
//
// No React / RN deps — these are pure functions over (text, selection).

export interface Selection {
	readonly start: number;
	readonly end: number;
}

export interface EditResult {
	/** Next body text. */
	readonly text: string;
	/** Next selection to apply to the input. */
	readonly selection: Selection;
}

/** Inline wrap markers handled by `toggleWrap`. */
export type WrapKind = "bold" | "italic" | "underline" | "strike" | "code";

const MARKERS: Record<WrapKind, { open: string; close: string }> = {
	bold: { open: "**", close: "**" },
	italic: { open: "*", close: "*" },
	// Markdown has no underline; `<u>…</u>` is the conventional fallback.
	underline: { open: "<u>", close: "</u>" },
	strike: { open: "~~", close: "~~" },
	code: { open: "`", close: "`" },
};

// Every delimiter string in the marker table (both open + close). Some
// delimiters are prefixes/suffixes of another (italic `*` is the head AND
// tail of bold `**`), so a naive `endsWith("*")` on `**` would misread a
// bold marker as an italic one and strip a single `*`, silently
// downgrading `**foo**` → `*foo*`. The helpers below only accept a
// boundary as `delim` when it is NOT part of a longer marker.
const ALL_DELIMS: readonly string[] = Object.values(MARKERS).flatMap((m) => [
	m.open,
	m.close,
]);

/** True when `s` ends with `delim` and that isn't the tail of a longer delim. */
function endsWithDelim(s: string, delim: string): boolean {
	if (!s.endsWith(delim)) return false;
	return !ALL_DELIMS.some(
		(d) => d.length > delim.length && d.endsWith(delim) && s.endsWith(d),
	);
}

/** True when `s` starts with `delim` and that isn't the head of a longer delim. */
function startsWithDelim(s: string, delim: string): boolean {
	if (!s.startsWith(delim)) return false;
	return !ALL_DELIMS.some(
		(d) => d.length > delim.length && d.startsWith(delim) && s.startsWith(d),
	);
}

/**
 * Toggle an inline wrap around the current selection. If the selection is
 * already wrapped with the markers (either inside or just outside), the
 * markers are stripped; otherwise they are added. With no selection the
 * markers are inserted at the caret with the caret placed between them.
 */
export function toggleWrap(
	text: string,
	sel: Selection,
	kind: WrapKind,
): EditResult {
	const { open, close } = MARKERS[kind];
	const start = Math.min(sel.start, sel.end);
	const end = Math.max(sel.start, sel.end);
	const selected = text.slice(start, end);

	// Already wrapped *inside* the selection → unwrap. Match THIS marker
	// exactly so a bold-wrapped `**foo**` isn't misread as italic and
	// stripped down to `*foo*`.
	if (
		startsWithDelim(selected, open) &&
		endsWithDelim(selected, close) &&
		selected.length >= open.length + close.length
	) {
		const inner = selected.slice(open.length, selected.length - close.length);
		const next = text.slice(0, start) + inner + text.slice(end);
		return {
			text: next,
			selection: { start, end: start + inner.length },
		};
	}

	// Wrapped *just outside* the selection → unwrap. Only when the adjacent
	// delimiters are exactly THIS marker — `before` ending in `**` (bold)
	// must not be consumed one `*` at a time by an italic toggle.
	const before = text.slice(0, start);
	const after = text.slice(end);
	if (endsWithDelim(before, open) && startsWithDelim(after, close)) {
		const next =
			before.slice(0, before.length - open.length) +
			selected +
			after.slice(close.length);
		const ns = start - open.length;
		return { text: next, selection: { start: ns, end: ns + selected.length } };
	}

	// No selection → insert markers, caret between them.
	if (start === end) {
		const next = before + open + close + after;
		const caret = start + open.length;
		return { text: next, selection: { start: caret, end: caret } };
	}

	// Wrap the selection.
	const next = before + open + selected + close + after;
	const ns = start + open.length;
	return { text: next, selection: { start: ns, end: ns + selected.length } };
}

/**
 * Toggle a line prefix on every line overlapping the selection. Used for
 * bulleted (`- `), numbered (`1. `) and quote/indent prefixes. If every
 * targeted line already has the prefix it is removed; otherwise it is
 * added to all of them. Numbered lists renumber sequentially.
 */
export function toggleLinePrefix(
	text: string,
	sel: Selection,
	kind: "bullet" | "ordered" | "indent",
): EditResult {
	const start = Math.min(sel.start, sel.end);
	const end = Math.max(sel.start, sel.end);

	// Expand selection to whole lines.
	const lineStart = text.lastIndexOf("\n", start - 1) + 1;
	let lineEnd = text.indexOf("\n", end);
	if (lineEnd === -1) lineEnd = text.length;

	const block = text.slice(lineStart, lineEnd);
	const lines = block.split("\n");

	const bulletRe = /^(\s*)- /;
	const orderedRe = /^(\s*)\d+\. /;
	const indentRe = /^(\t| {2})/;

	const re =
		kind === "bullet" ? bulletRe : kind === "ordered" ? orderedRe : indentRe;
	const allPrefixed = lines.every((l) => l.length === 0 || re.test(l));

	let outLines: string[];
	if (allPrefixed) {
		// Remove the prefix.
		outLines = lines.map((l) => l.replace(re, "$1"));
	} else if (kind === "bullet") {
		outLines = lines.map((l) => (l.length === 0 ? l : `- ${l}`));
	} else if (kind === "ordered") {
		let n = 0;
		outLines = lines.map((l) => {
			if (l.length === 0) return l;
			n += 1;
			return `${n}. ${l}`;
		});
	} else {
		outLines = lines.map((l) => (l.length === 0 ? l : `  ${l}`));
	}

	const replaced = outLines.join("\n");
	const next = text.slice(0, lineStart) + replaced + text.slice(lineEnd);
	return {
		text: next,
		selection: { start: lineStart, end: lineStart + replaced.length },
	};
}

/**
 * Insert a fenced code block around the selection (or at the caret). The
 * fence sits on its own lines; the selected text becomes the block body.
 */
export function insertCodeBlock(text: string, sel: Selection): EditResult {
	const start = Math.min(sel.start, sel.end);
	const end = Math.max(sel.start, sel.end);
	const selected = text.slice(start, end);
	const before = text.slice(0, start);
	const after = text.slice(end);

	// Ensure the fence starts on a fresh line.
	const leadingNL = before.length === 0 || before.endsWith("\n") ? "" : "\n";
	const trailingNL = after.length === 0 || after.startsWith("\n") ? "" : "\n";

	const block = `${leadingNL}\`\`\`\n${selected}\n\`\`\`${trailingNL}`;
	const next = before + block + after;
	// Place caret on the (empty) body line when nothing was selected.
	const caret = before.length + leadingNL.length + 4; // after "```\n"
	if (start === end) {
		return { text: next, selection: { start: caret, end: caret } };
	}
	return {
		text: next,
		selection: { start: caret, end: caret + selected.length },
	};
}

/**
 * Insert a markdown link. `url` is required; when text is selected it
 * becomes the link label, otherwise a placeholder label is inserted and
 * left selected for the caller to overtype.
 */
export function insertLink(
	text: string,
	sel: Selection,
	url: string,
): EditResult {
	const start = Math.min(sel.start, sel.end);
	const end = Math.max(sel.start, sel.end);
	const selected = text.slice(start, end);
	const label = selected.length > 0 ? selected : "text";
	const before = text.slice(0, start);
	const after = text.slice(end);
	const md = `[${label}](${url})`;
	const next = before + md + after;
	// Select the label so it can be overtyped when it's the placeholder.
	const labelStart = start + 1;
	return {
		text: next,
		selection: { start: labelStart, end: labelStart + label.length },
	};
}
